import { gql, useMutation, useQuery } from '@apollo/client'

import {
  CollectionsSelectBoxFragmentDoc,
  ConsoleCollectionEditQuery,
  ConsoleCollectionEditQueryVariables,
  UpdateCollectionMutation,
  UpdateCollectionMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, routerPages, generatePath } =
    useRouter<{ collectionId: string }>()
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('consoleCollectionEdit')
  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(user?.uid || '')

  const { data, loading } = useQuery<
    ConsoleCollectionEditQuery,
    ConsoleCollectionEditQueryVariables
  >(QUERY, { variables: { id: router.query.collectionId } })

  const [updateCollection, { loading: updating }] = useMutation<
    UpdateCollectionMutation,
    UpdateCollectionMutationVariables
  >(UPDATE_COLLECTION, {
    onCompleted() {
      showMessage(t('editCollectionSuccessful'), 'success')
      router.goBack(
        generatePath(routerPages.ConsoleCollectionDetail, {
          collectionId: router.query.collectionId,
        })
      )
    },
  })

  const onDrop = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit.image) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      artistId: user?.uid ?? '',
      progress,
      loading,
      updating,
      collection: data?.collection ?? null,
    },
    actions: {
      async onSubmit({
        title,
        description,
        thumbnailPath,
        isPublishOnStore,
        startedAt,
        endedAt,
        status,
      }) {
        await updateCollection({
          variables: {
            input: {
              id: router.query.collectionId,
              title,
              description,
              thumbnailPath,
              isPublishOnStore,
              startedAt,
              endedAt,
              status,
            },
          },
        })
      },
      async onDrop() {
        const file = await onDrop().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file, false, true)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
    },
  }
})

const QUERY = gql`
  query ConsoleCollectionEdit($id: ID!) {
    collection(id: $id) {
      ...CollectionsSelectBox
    }
  }
  ${CollectionsSelectBoxFragmentDoc}
`

const UPDATE_COLLECTION = gql`
  mutation UpdateCollection($input: UpdateCollectionInput) {
    updateCollection(inputs: $input) {
      ...CollectionsSelectBox
    }
  }
  ${CollectionsSelectBoxFragmentDoc}
`
